import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile + 2 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons de déménagement livrés à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Épargnez-vous la manutention fastidieuse et l&apos;encombrement
            d&apos;un coffre de voiture saturé par des liasses de cartons
            bruts. Nous prenons en charge l&apos;intégralité du transit
            jusqu&apos;à votre pas de porte à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Maîtrise absolue du circuit",
              body: "Nous refusons la sous-traitance à des réseaux de messagerie généraux. C'est notre propre structure qui charge et dépose vos fournitures, garantissant un traitement soigné du matériel.",
              Icon: Truck,
            },
            {
              title: "Logistique de proximité exclusive",
              body: "Notre rayon d'action est volontairement verrouillé sur l'Île-de-France. Cela nous permet d'assurer une réactivité maximale, de respecter rigoureusement les créneaux convenus et de vous livrer des produits stockés à l'abri de l'humidité, prêts pour l'assemblage.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Comment commander : 3 étapes numérotées ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            En 3 étapes
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment commander vos cartons de déménagement&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous avons simplifié notre plateforme pour vous permettre de vous
            équiper en un temps record.
          </p>
        </div>

        <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "01",
              title: "Composez votre panier",
              body: "Parcourez notre catalogue et sélectionnez vos cartons à l'unité, vos protections (film bulle, couvertures) ou optez directement pour l'un de nos packs complets adaptés à la taille de votre logement.",
            },
            {
              n: "02",
              title: "Renseignez vos coordonnées",
              body: `Validez vos choix et indiquez votre adresse de livraison précise à ${city.name}.`,
            },
            {
              n: "03",
              title: "Planifiez la réception",
              body: "Notre service logistique prend immédiatement le relais pour programmer le passage de notre véhicule selon vos disponibilités.",
            },
          ].map(({ n, title, body }) => (
            <li
              key={n}
              className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="absolute -top-4 left-5 inline-flex items-center justify-center h-9 px-3 rounded-full bg-[#237cb5] text-white font-display font-extrabold text-[13px] tracking-[0.1em]">
                ÉTAPE {n}
              </div>
              <h3 className="mt-3 font-display font-extrabold text-[16px] text-[#0f1d2b]">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-10">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-courbevoie-cta-shop"
          >
            Commencer ma commande
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Paiement sécurisé 100% ======= */}
    <section className="py-14 md:py-20 bg-[#0f1d2b] text-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-bold text-white/80 mb-4">
              <Lock className="h-3.5 w-3.5" strokeWidth={2.6} />
              Sécurité maximale
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px]">
              Achetez l&apos;esprit libre : un paiement sécurisé à 100&nbsp;%
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-white/80 leading-relaxed">
              La sécurité de vos transactions est notre priorité absolue. Lors
              de la validation de votre commande, vos données bancaires sont
              intégralement protégées grâce à des protocoles de cryptage SSL
              de dernière génération.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-white/80 leading-relaxed">
              Que vous choisissiez de régler par carte bancaire ou via nos
              solutions partenaires, l&apos;ensemble du processus est
              totalement hermétique aux tentatives de fraude. Vous achetez
              vos fournitures en toute transparence, sans le moindre
              compromis sur la confidentialité de vos informations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { title: "Cryptage SSL", body: "Toutes les transactions chiffrées de bout en bout.", Icon: Lock },
              { title: "Carte bancaire", body: "Visa, Mastercard et solutions partenaires acceptées.", Icon: CreditCard },
              { title: "0 % de fraude tolérée", body: "Processus hermétique aux tentatives malveillantes.", Icon: ShieldCheck },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/15 p-4"
              >
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="mt-3 font-display font-extrabold text-[14.5px] text-white">
                  {title}
                </h3>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-courbevoie-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
